import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import api from "../../lib/api";
import useRevealOnScroll from "../../lib/useRevealOnScroll";

const inr = (n) => (n ? `₹${Number(n).toLocaleString("en-IN")}` : null);

// Flagship, high-intent journeys we want landing traffic funnelled to first.
// Matched by name so admins can reorder/feature freely without code changes.
const PRIORITY = ["tiger safari", "northern lights", "antarctica", "hornbill", "rann utsav", "meghalaya"];

// Static flagship card appended so the grid always fills 2 clean rows of 3.
const WINE_CARD = {
    id: "wine-odyssey-static",
    name: "Epic Global Wine Odyssey",
    location: "Eight Wine Regions · Four Continents",
    short_description: "32 customisable nights from Nashik to Saint-Émilion, the Douro, Tuscany, Scotland and the Cape — with your personal sommelier.",
    image_url: "/api/assets/img/wine-odyssey-premium.jpg",
    href: "/tours/wine-odyssey",
    price_label: "from $75,000",
};

function rank(dest) {
    const n = (dest.name || "").toLowerCase();
    const i = PRIORITY.findIndex((p) => n.includes(p));
    return i === -1 ? PRIORITY.length : i;
}

export const SignatureExperiences = ({ onEnquire }) => {
    const [items, setItems] = useState([]);
    const reveal = useRevealOnScroll();

    useEffect(() => {
        api.get("/destinations")
            .then((res) => {
                const matched = (res.data || []).filter((d) => rank(d) < PRIORITY.length);
                matched.sort((a, b) => rank(a) - rank(b));
                setItems([...matched.slice(0, 5), WINE_CARD]);
            })
            .catch(() => {});
    }, []);

    if (items.length === 0) return null;

    return (
        <section ref={reveal} className="section-padding bg-[#0d1210] text-[#f2efe6] relative overflow-hidden" data-testid="signature-experiences-section">
            <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 15% 20%, #d8c9a3 0, transparent 42%), radial-gradient(circle at 85% 80%, #4a7c59 0, transparent 46%)" }} aria-hidden="true" />
            <div className="container-page relative">
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-14 reveal-stagger">
                    <div>
                        <p className="overline text-[#c9a44c] mb-4 flex items-center gap-2">
                            <Sparkles size={14} strokeWidth={1.5} /> Signature Experiences
                        </p>
                        <h2 className="font-serif text-4xl sm:text-5xl tracking-tight leading-tight max-w-2xl">
                            Our rarest journeys, once in a lifetime.
                        </h2>
                    </div>
                    <Link
                        to="/destinations"
                        data-testid="signature-view-all"
                        className="text-xs tracking-[0.25em] uppercase font-semibold text-[#c9a44c] hover:text-[#f2efe6] transition-colors inline-flex items-center gap-2"
                    >
                        Explore all journeys <ArrowRight size={14} strokeWidth={1.5} />
                    </Link>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
                    {items.map((d, idx) => (
                        <Link
                            key={d.id}
                            to={d.href || `/destinations/${d.slug || d.id}`}
                            data-testid={`signature-card-${idx}`}
                            className="group relative block overflow-hidden rounded-lg h-[440px] reveal-stagger"
                            style={{ animationDelay: `${idx * 90}ms` }}
                        >
                            <img
                                src={d.image_url}
                                alt={d.name}
                                loading="lazy"
                                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />
                            {idx === 0 && (
                                <span className="absolute top-4 left-4 text-[10px] tracking-[0.25em] uppercase font-semibold bg-[#c9a44c] text-[#0d1210] px-3 py-1.5 rounded-full">
                                    Flagship
                                </span>
                            )}
                            <div className="absolute inset-x-0 bottom-0 p-6">
                                <p className="overline text-[#f2efe6]/70 mb-2">{d.location}</p>
                                <h3 className="font-serif text-2xl text-[#f2efe6] leading-snug">{d.name}</h3>
                                <p className="mt-2 text-sm text-[#f2efe6]/75 line-clamp-2">{d.short_description}</p>
                                <div className="mt-4 flex items-center justify-between gap-3">
                                    {d.price_label ? (
                                        <span className="text-sm text-[#f2efe6]/90">
                                            <span className="text-[#f2efe6]/60">from </span>{d.price_label.replace("from ", "")}
                                        </span>
                                    ) : inr(d.starting_price) && (
                                        <span className="text-sm text-[#f2efe6]/90">
                                            <span className="text-[#f2efe6]/60">from </span>{inr(d.starting_price)}
                                        </span>
                                    )}
                                    <span className="text-xs tracking-[0.2em] uppercase font-semibold text-[#c9a44c] inline-flex items-center gap-1.5 group-hover:gap-2.5 transition-all">
                                        Discover <ArrowRight size={13} strokeWidth={1.75} />
                                    </span>
                                </div>
                                {onEnquire && (
                                    <button
                                        type="button"
                                        data-testid={`signature-enquire-${idx}`}
                                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onEnquire(d.name); }}
                                        className="mt-4 w-full py-2.5 border border-[#c9a44c]/60 text-[#c9a44c] text-[10px] tracking-[0.25em] uppercase font-semibold hover:bg-[#c9a44c] hover:text-[#0d1210] transition-colors rounded-full"
                                    >
                                        Enquire about this journey
                                    </button>
                                )}
                            </div>
                        </Link>
                    ))}
                </div>
            </div>
        </section>
    );
};
